/**
 * DesignTools - the front door for the three design applications, the web counterpart of the
 * desktop AE Suite launcher. Each tile is a plain link: the apps are separate screens that
 * main.tsx picks by path, so a full page load is exactly what is wanted.
 */

import { useEffect, useState } from "react";
import { userFrom } from "@plan-forge/entra-auth";
import { authRequired, initialAuthState, signIn, signOut, type AuthState } from "./auth.ts";
import { SignIn } from "./SignIn.tsx";
import "./designtools.css";

interface Tool {
  readonly href: string;
  readonly icon: string;
  readonly title: string;
  readonly product: string;
  readonly description: string;
}

const TOOLS: readonly Tool[] = [
  {
    href: "/speclens",
    icon: "\u{1F50D}",
    title: "Analyze Specs / Construction Docs",
    product: "AE SpecLens",
    description: "Match a specification against AE standards and draft RFIs.",
  },
  {
    href: "/templatecreator",
    icon: "\u{1F4D0}",
    title: "Create Project Template",
    product: "AE Template Creator",
    description: "Read a 03 drawing and build the 01 template package.",
  },
  {
    href: "/planforge",
    icon: "\u{1F5FA}\u{FE0F}",
    title: "Convert Floor Plans",
    product: "AE PlanForge",
    description: "Turn a floor plan into a drawing for AutoCAD.",
  },
];

export function DesignToolsApp() {
  const [auth, setAuth] = useState<AuthState>({ kind: authRequired ? "loading" : "disabled" });

  useEffect(() => {
    document.title = "DesignTools";
  }, []);

  useEffect(() => {
    if (!authRequired) return;
    void initialAuthState().then(setAuth);
  }, []);

  if (auth.kind === "loading") return <div className="booting">Checking your sign-in...</div>;
  if (auth.kind === "signed-out") {
    return <SignIn reason={auth.reason} onSignIn={() => void signIn()} />;
  }

  const user = auth.kind === "signed-in" ? userFrom(auth.session) : null;

  return (
    <main className="designtools">
      <header className="designtools__header">
        <div>
          <h1 className="designtools__title">DesignTools</h1>
          <p className="designtools__subtitle">Pick a tool.</p>
        </div>
        {auth.kind === "signed-in" && (
          <div className="designtools__user">
            <span title={user?.username ?? undefined}>{user?.name ?? user?.username ?? "Signed in"}</span>
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => {
                signOut();
                setAuth({ kind: "signed-out" });
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </header>

      <nav className="designtools__tiles" aria-label="Design tools">
        {TOOLS.map((tool) => (
          <a key={tool.href} className="designtools__tile" href={tool.href}>
            <span className="designtools__icon" aria-hidden="true">
              {tool.icon}
            </span>
            <span className="designtools__tile-title">{tool.title}</span>
            <span className="designtools__product">{tool.product}</span>
            <span className="designtools__description">{tool.description}</span>
          </a>
        ))}
      </nav>
    </main>
  );
}
